import { useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { FadeIn, StaggerContainer, StaggerItem } from '../components/Animations';
import { getMobWikiUrl } from '../data/wiki_mobs_assets';
import './WikiItemList.css'; // Re-use the same CSS for grid styles
import { McIcon } from '../components/CraftingGrid';

const VIDEO = 'https://pub-332780ae20b540038a9ea772f1bf747c.r2.dev/background_vids/vid_1.mp4';

const RARITY_COLORS = {
  common: '#9e9e9e',
  uncommon: '#52c078',
  rare: '#5a9ee0',
  elite: '#a070e0',
  epic: '#c060d0',
  legendary: '#f0ab20',
  boss: '#e84c3d',
  // Numeric fallbacks from API
  0: '#9e9e9e',
  1: '#009133',
  2: '#5a9ee0',
  3: '#ff8800',
  4: '#560079',
};

const RARITY_GLOW = {
  common: 'rgba(154,144,128,0.12)',
  uncommon: 'rgba(82,192,120,0.12)',
  rare: 'rgba(90,158,224,0.12)',
  elite: 'rgba(160,112,224,0.12)',
  epic: 'rgba(192,96,208,0.12)',
  legendary: 'rgba(240,171,32,0.12)',
  boss: 'rgba(232,76,61,0.12)',
  0: 'rgba(154,144,128,0.12)',
  1: 'rgba(82,192,120,0.12)',
  2: 'rgba(90,158,224,0.12)',
  3: 'rgba(133, 98, 4, 0.12)',
  4: 'rgba(86, 0, 121, 0.12)',
};

const RARITY_BORDER = {
  common: 'rgba(154,144,128,0.28)',
  uncommon: 'rgba(82,192,120,0.28)',
  rare: 'rgba(90,158,224,0.28)',
  elite: 'rgba(160,112,224,0.28)',
  epic: 'rgba(192,96,208,0.28)',
  legendary: 'rgba(240,171,32,0.28)',
  boss: 'rgba(232,76,61,0.28)',
  0: 'rgba(154,144,128,0.28)',
  1: 'rgba(82,192,120,0.28)',
  2: 'rgba(90,158,224,0.28)',
  3: 'rgba(133, 98, 4, 0.28)',
  4: 'rgba(86, 0, 121, 0.28)',
};

const CATEGORY_MC_ICONS = {
  All: 'book',
  Boss: 'wither_skeleton_skull',
  Undead: 'zombie_head',
  Beast: 'bone',
  Arthropod: 'cobweb',
  Illager: 'crossbow',
  Construct: 'iron_block',
};

const ALL_CATEGORIES = ['All', 'Boss', 'Undead', 'Beast', 'Arthropod', 'Illager', 'Construct'];

// Map mob types/names to categories
const GET_MOB_CATEGORY = (mob) => {
  const t = (mob.mob_type || mob.type || mob.name || '').toLowerCase();
  if (t.includes('zombie') || t.includes('skeleton') || t.includes('drowned') || t.includes('stray') || t.includes('husk') || t.includes('phantom') || t.includes('wither') || t.includes('ghast') || t.includes('zoglin') || t.includes('revenant') || t.includes('bone') || t.includes('lich') || t.includes('knight') || t.includes('general') || t.includes('grunt') || t.includes('soul') || t.includes('maniac') || t.includes('undead')) return 'Undead';
  if (t.includes('wolf') || t.includes('bee') || t.includes('cow') || t.includes('rabbit') || t.includes('hoglin') || t.includes('piglin') || t.includes('brute') || t.includes('ravager') || t.includes('kraken') || t.includes('bull') || t.includes('wasp') || t.includes('hornet') || t.includes('hog') || t.includes('werewolf') || t.includes('pup') || t.includes('lorax') || t.includes('sprout') || t.includes('hydra') || t.includes('sovereign')) return 'Beast';
  if (t.includes('spider') || t.includes('endermite') || t.includes('silverfish') || t.includes('crawler') || t.includes('scuttler') || t.includes('scratcher') || t.includes('scrambler') || t.includes('spiderling') || t.includes('tarantula')) return 'Arthropod';
  if (t.includes('pillager') || t.includes('vindicator') || t.includes('evoker') || t.includes('vex') || t.includes('goblin') || t.includes('traveler')) return 'Illager';
  if (t.includes('golem') || t.includes('guardian') || t.includes('warden') || t.includes('breeze') || t.includes('shard') || t.includes('ore') || t.includes('bedrock') || t.includes('stone') || t.includes('colossus') || t.includes('titan') || t.includes('behemoth') || t.includes('giant') || t.includes('troll') || t.includes('pillar')) return 'Construct';
  return 'Other';
};

export default function WikiMobList({ items = [], mobs = [] }) {
  const [activeCategory, setActiveCategory] = useState('All');
  const [searchParams] = useSearchParams();
  const q = (searchParams.get('q') || '').toLowerCase().trim();

  const getTierRank = (tier) => {
    if (tier === 'boss') return 5;
    const parsed = Number(tier);
    return Number.isFinite(parsed) ? parsed : 0;
  };

  const enabledMobs = mobs.filter(m => m.enabled !== 0 && m.enabled !== '0');

  let filtered = activeCategory === 'All'
    ? enabledMobs
    : enabledMobs.filter(m => {
      const cat = GET_MOB_CATEGORY(m);
      return cat === activeCategory || (activeCategory === 'Boss' && (m.tier === 'boss' || m.tier >= 4));
    });

  if (q) {
    filtered = filtered.filter(m =>
      m.name.toLowerCase().includes(q) ||
      m.type?.toLowerCase().includes(q)
    );
  }

  filtered = [...filtered].sort((a, b) => {
    const tierDelta = getTierRank(b.tier) - getTierRank(a.tier);
    if (tierDelta !== 0) return tierDelta;
    return a.name.localeCompare(b.name);
  });

  return (
    <div className="wil-root">

      {/* ── Hero with video ── */}
      <div className="wil-hero">
        <video className="wil-hero-vid" src={VIDEO} autoPlay muted loop playsInline />
        <div className="wil-hero-overlay" />
        <FadeIn className="wil-hero-content container" y={30} duration={0.8}>
          <div className="wil-hero-eyebrow">SimpleMC Wiki</div>
          <h1 className="wil-hero-title">Custom <em>Mobs</em></h1>
          <p className="wil-hero-sub">
            Unique creatures with custom drops. Spawn conditions, drop tables, and combat tips.
          </p>
          <div className="wil-hero-stats">
            <div className="wil-hero-stat">
              <span className="wil-hero-stat-num">{enabledMobs.length}</span>
              <span className="wil-hero-stat-lbl">Total Mobs</span>
            </div>
            <div className="wil-hero-stat-div" />
            <div className="wil-hero-stat">
              <span className="wil-hero-stat-num">{enabledMobs.filter(m => m.tier === '1' || m.tier == 1 ).length}</span>
              <span className="wil-hero-stat-lbl">Tier 1</span>
            </div>
            <div className="wil-hero-stat-div" />
            <div className="wil-hero-stat">
              <span className="wil-hero-stat-num">{enabledMobs.filter(m => m.tier === '2' || m.tier == 2 ).length}</span>
              <span className="wil-hero-stat-lbl">Tier 2</span>
            </div>
            <div className="wil-hero-stat-div" />
            <div className="wil-hero-stat">
              <span className="wil-hero-stat-num">{enabledMobs.filter(m => m.tier === '3' || m.tier == 3 ).length}</span>
              <span className="wil-hero-stat-lbl">Tier 3</span>
            </div>
            <div className="wil-hero-stat-div" />
            <div className="wil-hero-stat">
              <span className="wil-hero-stat-num">{enabledMobs.filter(m => m.tier === '4' || m.tier == 4 ).length}</span>
              <span className="wil-hero-stat-lbl">Tier 4</span>
            </div>
          </div>
        </FadeIn>
      </div>

      <div className="container">

        {/* ── Filter bar ── */}
        <div className="wil-filter-bar">
          <div className="wil-filter-pills">
            {ALL_CATEGORIES.map(cat => (
              <button
                key={cat}
                className={`wil-filter-btn${activeCategory === cat ? ' active' : ''}`}
                onClick={() => setActiveCategory(cat)}
              >
                <McIcon materialKey={CATEGORY_MC_ICONS[cat] || 'zombie'} size={20} />
                {cat}
              </button>
            ))}
          </div>
          <span className="wil-count">
            <strong>{filtered.length}</strong> {filtered.length === 1 ? 'mob' : 'mobs'}
          </span>
        </div>

        {/* ── Grid ── */}
        {filtered.length === 0 ? (
          <div className="wil-empty">
            <McIcon materialKey="zombie_head" size={48} />
            <p>No mobs found in this category yet.</p>
          </div>
        ) : (
          <StaggerContainer className="wil-grid">
            {filtered.map(mob => {
              const rarityColor = RARITY_COLORS[mob.tier] || '#9e9e9e';
              const rarityGlow = RARITY_GLOW[mob.tier] || 'rgba(255,255,255,0.04)';
              const rarityBorder = RARITY_BORDER[mob.tier] || 'rgba(255,255,255,0.08)';
              const mobIcon = getMobWikiUrl(mob.mob_type || mob.type, mob.name);
              return (
                <StaggerItem key={mob.id} style={{ height: '100%' }}>
                  <Link
                    to={`/wiki/mob/${mob.slug}`}
                    className="wil-card"
                    style={{
                      '--rarity-color': rarityColor,
                      '--rarity-glow': rarityGlow,
                      '--rarity-border': rarityBorder,
                    }}
                  >
                    {/* Rarity accent bar on top */}
                    <div className="wil-card-accent-bar" />

                    <div className="wil-card-top">
                      <div className="wil-card-icon-wrap">
                        <div className="wil-card-icon">
                          {mob.icon && mob.icon.startsWith('http') ? (
                            <img src={mob.icon} alt={mob.name} style={{ width: '50px', height: '50px', objectFit: 'contain' }} />
                          ) : mobIcon ? (
                            <img src={mobIcon} alt={mob.name} style={{ width: '50px', height: '50px', objectFit: 'contain' }} />
                          ) : (
                            mob.icon || '🐾'
                          )}
                        </div>
                        <div className="wil-card-icon-glow" />
                      </div>
                      <div className="wil-card-title-block">
                        <div className="wil-card-name">{mob.name}</div>
                        <div className="wil-card-type-row">
                          <span className="wil-rarity-badge" style={{ color: rarityColor }}>
                            {mob.tier === 0 ? 'Minion' : `Tier ${mob.tier}`}
                          </span>
                          <span className="wil-card-sep">·</span>
                          <span className="wil-card-category-badge">{GET_MOB_CATEGORY(mob)}</span>
                        </div>
                      </div>
                    </div>

                    {mob.type && <div className="wil-card-type-label">Original: {mob.type}</div>}

                    <div className="wil-card-desc">{mob.description}</div>

                    {/* Abilities preview */}
                    {mob.abilities?.length > 0 && (
                      <div className="wil-card-abilities">
                        {mob.abilities.slice(0, 2).map((ab, i) => (
                          <div key={i} className="wil-card-ability-pill">
                            <span className="wil-card-ability-name">{ab.name}</span>
                            {ab.cooldown && (
                              <span className="wil-card-ability-cd">{ab.cooldown}</span>
                            )}
                          </div>
                        ))}
                      </div>
                    )}

                    <div className="wil-card-footer">
                      <div className="wil-card-tags" style={{ display: 'flex', gap: '0.5rem', color: 'var(--text-muted)' }}>
                        {mob.hp != null && <span className="wil-tag">HP: {mob.hp}</span>}
                        {mob.damage != null && <span className="wil-tag">DMG: {mob.damage}</span>}
                      </div>
                      <span className="wil-card-arrow">›</span>
                    </div>
                  </Link>
                </StaggerItem>
              );
            })}
          </StaggerContainer>
        )}

      </div>
    </div>
  );
}
